"use client";

import { motion } from "framer-motion";
import dynamic from "next/dynamic";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import PremiumButton from "../../components/PremiumButton";
import { Zap, Rocket, MapPin } from "lucide-react";

// Lazy load the Contact component
const Contact = dynamic(() => import("../../components/Contact"));

const reasons = [
  {
    icon: <Zap className="w-6 h-6" />,
    title: "Custom built, not templated",
    description:
      "Every site is hand-coded from scratch. You get a design that matches your brand, not a template everyone else is using.",
  },
  {
    icon: <MapPin className="w-6 h-6" />,
    title: "Local but not limited",
    description:
      "Based in Pasadena, serving clients across California and nationwide. I understand how local businesses win.",
  },
  {
    icon: <Rocket className="w-6 h-6" />,
    title: "Built for speed",
    description:
      "Landing pages live in about a week. Custom sites in 2 to 8 weeks. I work with frontier AI models to move fast, and every deliverable is human-reviewed.",
  },
];

const servicePackages = [
  {
    name: "One-Page Landing Page",
    price: "$500",
    description:
      "A clean, fast single-page website to give your business a professional online presence. Live in 5 to 7 days.",
    features: [
      "Single-page responsive design",
      "Contact form included",
      "Mobile-optimized",
      "Live in 5-7 days",
    ],
    highlight: false,
  },
  {
    name: "Custom Website",
    price: "from $2,500",
    description:
      "A fully custom multi-page website built from scratch for your specific needs. Scope and timeline depend on the project.",
    features: [
      "Multiple pages",
      "Custom design & layout",
      "Contact forms & integrations",
      "2-8 week timeline",
    ],
    highlight: true,
  },
  {
    name: "Managed Hosting",
    price: "$100/mo",
    description:
      "I handle your hosting, security, and backups so you don't have to think about it.",
    features: [
      "Premium VPS hosting",
      "SSL certificate",
      "Daily backups",
      "Two edits monthly",
    ],
    highlight: false,
  },
];

const processSteps = [
  {
    step: "01",
    title: "Discovery Call",
    description: "A quick conversation about your goals and timeline.",
  },
  {
    step: "02",
    title: "Custom Design",
    description: "Your site designed from scratch, with a preview in days.",
  },
  {
    step: "03",
    title: "Perfect & Launch",
    description: "One revision round, then we go live on your domain.",
  },
];

const faqs = [
  {
    question: "Are you a freelance web designer in Pasadena?",
    answer:
      "Yes. I am Arthur Behesnilian, a web designer based in Pasadena. You work directly with me, so you get dedicated attention and reliable delivery.",
  },
  {
    question: "What makes you different from other Pasadena website developers?",
    answer:
      "Every site is hand-coded from scratch. No templates, no page builders. I focus on clean design, fast performance, and professional results.",
  },
  {
    question: "Do you only work with Pasadena businesses?",
    answer:
      "I love working with local Pasadena businesses, but I serve clients across California and nationwide.",
  },
  {
    question: "How do I get started?",
    answer:
      "Get in touch at behes.dev/contact and tell me about your project. I will get back to you within 24 hours.",
  },
];

export default function PasadenaWebDesignClient() {
  const year = 2026;

  return (
    <main className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)] font-sans">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col justify-center px-6 pt-32 pb-20 overflow-hidden">
        {/* Single ember glow */}
        <div className="absolute top-1/3 -left-32 w-[38rem] h-[38rem] rounded-full bg-[var(--brand-color)]/8 blur-[150px] pointer-events-none -z-10" />

        <motion.div
          className="max-w-6xl mx-auto w-full"
          initial={{ opacity: 0.001, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
        >
          <p className="eyebrow mb-6">
            <span className="text-[var(--brand-color)]">01</span> · Web Design
            in Pasadena
          </p>

          <h1
            className="font-display font-medium tracking-tight leading-[1.02] mb-8 max-w-4xl"
            style={{ fontSize: "clamp(2.75rem, 8vw, 7rem)" }}
          >
            Pasadena web design,{" "}
            <em className="italic text-[var(--brand-color)]">built</em> from
            scratch.
          </h1>

          <p className="text-lg md:text-xl text-[var(--text-secondary)] max-w-2xl mb-10 leading-relaxed">
            Custom websites for businesses and service brands. Hand-coded, fast,
            and professional. No templates, no page builders. I am Arthur
            Behesnilian, a web designer based in{" "}
            <span className="text-[var(--text-primary)]">Pasadena</span>, serving
            clients nationwide.
          </p>

          <div className="flex flex-col sm:flex-row items-start gap-4">
            <PremiumButton
              href="/contact"
              size="xl"
              gaEvent={{
                action: "cta_click",
                category: "Pasadena Page",
                label: "Get in Touch - Hero",
                parameters: {
                  button_text: "Get in Touch",
                  button_location: "pasadena_hero",
                  destination_url: "/contact",
                },
              }}
            >
              Get in Touch
            </PremiumButton>

            <PremiumButton
              href="/services"
              size="xl"
              variant="secondary"
              gaEvent={{
                action: "cta_click",
                category: "Pasadena Page",
                label: "View Services - Hero",
                parameters: {
                  button_text: "View Services",
                  button_location: "pasadena_hero_secondary",
                  destination_url: "/services",
                },
              }}
            >
              View Services
            </PremiumButton>
          </div>
        </motion.div>
      </section>

      {/* Why Choose Section */}
      <section className="py-24 px-6 bg-[var(--bg-secondary)] border-t border-[var(--border-color)]">
        <motion.div
          className="max-w-6xl mx-auto"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="max-w-3xl mb-14">
            <p className="eyebrow mb-6">
              <span className="text-[var(--brand-color)]">02</span> · Why Work
              With Me
            </p>
            <h2
              className="font-display font-medium tracking-tight leading-[1.05] mb-6"
              style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}
            >
              Why Pasadena businesses{" "}
              <em className="italic text-[var(--brand-color)]">choose</em>{" "}
              behes.dev.
            </h2>
            <p className="text-lg text-[var(--text-secondary)]">
              Clean, fast websites built from scratch. No templates, no page
              builders. Just custom code that looks great and loads fast.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-[var(--border-color)] border border-[var(--border-color)] rounded-sm overflow-hidden">
            {reasons.map((item, index) => (
              <motion.div
                key={index}
                className="p-8 bg-[var(--bg-tertiary)]/40 hover:bg-[var(--bg-tertiary)]/70 transition-colors duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <div className="text-[var(--brand-color)] mb-6">{item.icon}</div>
                <h3 className="text-xl font-display font-medium tracking-tight mb-3">
                  {item.title}
                </h3>
                <p className="text-[var(--text-secondary)] leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Services Section */}
      <section className="py-24 px-6 bg-[var(--bg-primary)] border-t border-[var(--border-color)]">
        <motion.div
          className="max-w-6xl mx-auto"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="max-w-3xl mb-14">
            <p className="eyebrow mb-6">
              <span className="text-[var(--brand-color)]">03</span> · Services
            </p>
            <h2
              className="font-display font-medium tracking-tight leading-[1.05] mb-6"
              style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}
            >
              Web design{" "}
              <em className="italic text-[var(--brand-color)]">services</em>.
            </h2>
            <p className="text-lg text-[var(--text-secondary)]">
              Simple, honest pricing. All sites include mobile optimization and
              fast loading performance.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {servicePackages.map((service, index) => (
              <motion.div
                key={index}
                className={`relative flex flex-col p-8 rounded-sm border transition-colors duration-300 ${
                  service.highlight
                    ? "border-[var(--brand-color)]/60 bg-[var(--bg-tertiary)]/60"
                    : "border-[var(--border-color)] bg-[var(--bg-tertiary)]/40 hover:border-[var(--brand-color)]/60"
                }`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                {service.highlight && (
                  <span className="absolute top-6 right-6 font-mono text-[0.65rem] uppercase tracking-[0.18em] text-[var(--brand-color)]">
                    Most popular
                  </span>
                )}
                <h3 className="text-2xl font-display font-medium tracking-tight mb-2">
                  {service.name}
                </h3>
                <p className="text-[var(--brand-color)] font-mono text-sm mb-5">
                  {service.price}
                </p>
                <p className="text-[var(--text-secondary)] mb-6 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-2 mb-8">
                  {service.features.map((feature, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-3 text-sm text-[var(--text-secondary)]"
                    >
                      <span className="text-[var(--brand-color)]">✦</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto">
                  <PremiumButton
                    href="/services"
                    size="md"
                    variant={service.highlight ? "primary" : "secondary"}
                    className="w-full"
                    gaEvent={{
                      action: "cta_click",
                      category: "Pasadena Services",
                      label: `${service.name} - Learn More`,
                      parameters: {
                        service_name: service.name,
                        button_location: "services_section",
                      },
                    }}
                  >
                    Learn More
                  </PremiumButton>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-10">
            <p className="text-[var(--text-secondary)]">
              All sites include mobile optimization and fast loading
              performance.{" "}
              <a
                href="/services"
                className="text-[var(--brand-color)] hover:underline"
              >
                View all services and pricing →
              </a>
            </p>
          </div>
        </motion.div>
      </section>

      {/* Process Section */}
      <section className="py-24 px-6 bg-[var(--bg-secondary)] border-t border-[var(--border-color)]">
        <motion.div
          className="max-w-6xl mx-auto"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="max-w-3xl mb-14">
            <p className="eyebrow mb-6">
              <span className="text-[var(--brand-color)]">04</span> · Process
            </p>
            <h2
              className="font-display font-medium tracking-tight leading-[1.05] mb-6"
              style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}
            >
              Your website in three{" "}
              <em className="italic text-[var(--brand-color)]">steps</em>.
            </h2>
            <p className="text-lg text-[var(--text-secondary)]">
              Timeline: 5 days to 8 weeks depending on the project.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {processSteps.map((item, index) => (
              <motion.div
                key={index}
                className="border-t border-[var(--border-color)] pt-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <div className="font-display text-5xl text-[var(--accent-color)] mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-display font-medium tracking-tight mb-3">
                  {item.title}
                </h3>
                <p className="text-[var(--text-secondary)] leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-6 bg-[var(--bg-primary)] border-t border-[var(--border-color)]">
        <motion.div
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-14">
            <p className="eyebrow mb-6">
              <span className="text-[var(--brand-color)]">05</span> · FAQ
            </p>
            <h2
              className="font-display font-medium tracking-tight leading-[1.05]"
              style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}
            >
              Common{" "}
              <em className="italic text-[var(--brand-color)]">questions</em>.
            </h2>
          </div>

          <div className="divide-y divide-[var(--border-color)] border-y border-[var(--border-color)]">
            {faqs.map((item, index) => (
              <motion.div
                key={index}
                className="py-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05, duration: 0.5 }}
              >
                <h3 className="text-lg font-display font-medium tracking-tight mb-3">
                  {item.question}
                </h3>
                <p className="text-[var(--text-secondary)] leading-relaxed">
                  {item.answer}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Final CTA Section */}
      <section className="py-28 px-6 bg-[var(--bg-secondary)] relative overflow-hidden border-t border-[var(--border-color)]">
        <div className="absolute -bottom-48 left-1/2 -translate-x-1/2 w-[42rem] h-[42rem] rounded-full bg-[var(--brand-color)]/8 blur-[140px] pointer-events-none" />

        <motion.div
          className="max-w-4xl mx-auto text-center relative z-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2
            className="font-display font-medium tracking-tight leading-[1.05] mb-6"
            style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}
          >
            Ready to get{" "}
            <em className="italic text-[var(--brand-color)]">started</em>?
          </h2>
          <p className="text-lg md:text-xl text-[var(--text-secondary)] mb-12 max-w-2xl mx-auto">
            Let's talk about your project and see how I can help.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <PremiumButton
              href="/contact"
              size="xl"
              gaEvent={{
                action: "cta_click",
                category: "Pasadena Page",
                label: "Get in Touch - Final CTA",
                parameters: {
                  button_text: "Get in Touch",
                  button_location: "pasadena_final_cta",
                  destination_url: "/contact",
                },
              }}
            >
              Get in Touch
            </PremiumButton>

            <PremiumButton
              href="/services"
              size="xl"
              variant="secondary"
              gaEvent={{
                action: "cta_click",
                category: "Pasadena Page",
                label: "View Services - Final CTA",
                parameters: {
                  button_text: "View Services",
                  button_location: "pasadena_final_cta_secondary",
                  destination_url: "/services",
                },
              }}
            >
              View Services
            </PremiumButton>
          </div>
        </motion.div>
      </section>

      <Contact backgroundOverride="bg-[var(--bg-primary)]" />
      <Footer year={year} />
    </main>
  );
}
